import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import Price from "@/components/Price";
import { fmt } from "@/components/CartDrawer";
import { Reveal } from "@/components/motion";

const ROWS = [
  ["width", "Banner Width"], ["height", "Banner Height"], ["posts", "T-Posts Required"],
  ["bars", "Telescoping Bars"], ["bungees", "Bungees Included"], ["wind", "Wind Rating"], ["use", "Ideal Application"],
];

const cdn = (src, w = 800) => (src && src.startsWith("http") ? `${src}${src.includes("?") ? "&" : "?"}width=${w}` : src);

export default function ComparePage() {
  const [kits, setKits] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setKits(r.data.filter((p) => p.compare))).catch(() => {});
  }, []);

  return (
    <div data-testid="compare-page">
      <PageHeader
        kicker="Compare All Kits"
        title="Choose a size for the location"
        sub="Four frame configurations cover everything from a 4 ft curbside banner to a 16 ft highway display — and a V-config that's perfect for intersections."
        image="/assets/img/w-2x8-img-5047.jpg"
      />
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-12 lg:py-24">
        {/* Desktop matrix */}
        <Reveal className="hidden lg:block">
          <div className="overflow-x-auto border border-girder" data-testid="kit-comparison-table">
            <table className="w-full min-w-[900px] border-collapse">
              <thead>
                <tr className="border-b border-girder bg-steel/60">
                  <th className="px-5 py-4 text-left font-mono text-xs uppercase tracking-widest text-slate-500">Spec</th>
                  {kits.map((k) => (
                    <th key={k.slug} className="px-5 py-4 text-left">
                      <Link to={`/products/${k.slug}`} className="group block" data-testid={`compare-head-${k.slug}`}>
                        <img src={cdn(k.images[0])} alt={k.name} className="mb-3 aspect-[4/3] w-full clip-corner-sm object-cover" />
                        <span className="font-display text-xl font-bold uppercase text-slate-100 group-hover:text-hivis">{k.name}</span>
                        <span className="mt-1 block"><Price product={k} /></span>
                      </Link>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map(([key, label]) => (
                  <tr key={key} className="border-b border-girder last:border-0">
                    <td className="px-5 py-3.5 font-mono text-xs uppercase tracking-wider text-slate-500">{label}</td>
                    {kits.map((k) => <td key={k.slug} className="px-5 py-3.5 text-sm text-slate-200">{k.compare[key]}</td>)}
                  </tr>
                ))}
                <tr>
                  <td className="px-5 py-4" />
                  {kits.map((k) => (
                    <td key={k.slug} className="px-5 py-4">
                      <Link to={`/products/${k.slug}`} data-testid={`compare-order-${k.slug}`} className="clip-corner-sm inline-flex items-center gap-2 bg-hivis px-4 py-2 font-display text-sm font-bold uppercase text-obsidian transition-colors hover:bg-hivis-hover">
                        Order kit <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </Reveal>

        {/* Mobile cards */}
        <div className="grid gap-6 lg:hidden" data-testid="kit-comparison-cards">
          {kits.map((k, i) => (
            <Reveal key={k.slug} i={i} className="border border-girder bg-steel/50">
              <img src={cdn(k.images[0])} alt={k.name} className="aspect-[16/9] w-full clip-corner object-cover" />
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-2xl font-bold uppercase text-slate-100">{k.name}</h3>
                  <Price product={k} />
                </div>
                <dl className="mt-4 space-y-2">
                  {ROWS.map(([key, label]) => (
                    <div key={key} className="flex justify-between gap-4 border-b border-girder pb-2 text-sm last:border-0">
                      <dt className="font-mono text-[11px] uppercase tracking-wider text-slate-500">{label}</dt>
                      <dd className="text-right text-slate-200">{k.compare[key]}</dd>
                    </div>
                  ))}
                </dl>
                <Link to={`/products/${k.slug}`} data-testid={`compare-order-m-${k.slug}`} className="clip-corner-sm mt-5 inline-flex items-center gap-2 bg-hivis px-5 py-2.5 font-display font-bold uppercase text-obsidian">
                  Order kit <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-14">
          <div className="clip-corner border border-hivis/40 bg-steel/50 p-8 text-center sm:p-10" data-testid="compare-help-band">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-hivis">Still Deciding?</p>
            <h3 className="mt-3 font-display text-3xl font-black uppercase text-slate-100">Send us a photo of the location</h3>
            <p className="mx-auto mt-2 max-w-xl text-sm text-slate-400">We'll recommend the right kit and post layout — usually within one business day.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to="/contact" data-testid="compare-contact-cta" className="clip-corner-sm bg-hivis px-6 py-3 font-display font-bold uppercase text-obsidian transition-colors hover:bg-hivis-hover">Get a recommendation</Link>
              <Link to="/corporate" data-testid="compare-corporate-cta" className="clip-line-sm px-6 py-3 font-display font-bold uppercase text-slate-200 transition-colors hover:text-hivis hover:[--cb-line:#F59E0B]">Multi-location? Start here</Link>
              <Link to="/frame-comparison" data-testid="compare-frames-link" className="px-6 py-3 font-mono text-xs uppercase tracking-widest text-slate-400 underline decoration-girder underline-offset-4 transition-colors hover:text-hivis">Compare frame types</Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
